import React, { useState, ChangeEvent } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  FormControl,
  InputGroup,
  TextInput,
  Dropdown,
  ImageUpload,
  Loader,
} from 'components'
import {
  findSelected,
  formatPhoneNumberOnBlur,
  getPhoneNumberMaxLength,
  validateKeyPress,
  handleDisableOnPaste,
  thaiCountryCodeOption,
} from 'helpers/common'
import { ROLE_OPTIONS } from 'helpers/constants/common'
import { OptionProps } from 'types'

const ProfileForm: React.FC = () => {
  const [codeChecking, setCodeChecking] = useState<boolean>(false)
  const [nicknameChecking, setNicknameChecking] = useState<boolean>(false)
  const [firstNameChecking, setFirstNameChecking] = useState<boolean>(false)
  const [lastNameChecking, setLastNameChecking] = useState<boolean>(false)
  const [roleChecking, setRoleChecking] = useState<boolean>(false)

  const { control, errors, setValue, trigger } = useFormContext()
  const [avatar, setAvatar] = useState('')
  const { t } = useTranslation(['common', 'profile'])

  const base = 'f-profile__form'
  const roleOptions = ROLE_OPTIONS.filter(
    (item) => item.label !== t('common:all.label'),
  )

  const handleChangeAvatar = (data: Blob) => {
    setValue('imagePath', data)
    setAvatar(URL.createObjectURL(data))
  }

  const handleChangeRole = (value: OptionProps) => {
    const key = value.key

    setValue('role', key)
  }

  return (
    <div className={base}>
      <div className={`${base}__avatar`}>
        <span className={`${base}__avatar--title`}>
          {t('profile:profileInformation')}
        </span>
        <FormControl label={t('profile:uploadProfileImage.title')}>
          <Controller
            control={control}
            name='imagePath'
            render={(fields) => (
              <ImageUpload
                onChange={handleChangeAvatar}
                src={avatar || fields.value}
                placeholder={t('common:edit.button')}
              />
            )}
          />
        </FormControl>
      </div>

      <div className={`${base}__info`}>
        <FormControl label={t('profile:staffId.title')} required>
          <Controller
            type='text'
            name='code'
            control={control}
            render={(field) => (
              <TextInput
                value={field.value}
                onChange={field.onChange}
                onBlur={async () => {
                  setCodeChecking(true)
                  await trigger('code')
                  setCodeChecking(false)
                }}
                placeholder={t('profile:insertStaffId.placeholder')}
                isError={Boolean(errors.code?.message)}
                message={errors.code?.message}
                withIcon={codeChecking && <Loader />}
                ref={field.ref}
                disabled
              />
            )}
          />
        </FormControl>

        <div className={`${base}__name`}>
          <FormControl label={t('profile:nickname.title')} required>
            <Controller
              type='text'
              name='nickname'
              control={control}
              render={(field) => (
                <TextInput
                  className={`${base}__filed-name`}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={async () => {
                    setNicknameChecking(true)
                    await trigger('nickname')
                    setNicknameChecking(false)
                  }}
                  placeholder={t('profile:insertNickname.placeholder')}
                  isError={Boolean(errors.nickname?.message)}
                  message={errors.nickname?.message}
                  withIcon={nicknameChecking && <Loader />}
                  ref={field.ref}
                  disabled
                />
              )}
            />
          </FormControl>

          <FormControl label={t('profile:firstName.title')} required>
            <Controller
              type='text'
              name='firstName'
              control={control}
              render={(field) => (
                <TextInput
                  className={`${base}__filed-name`}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={async () => {
                    setFirstNameChecking(true)
                    await trigger('firstName')
                    setFirstNameChecking(false)
                  }}
                  placeholder={t('profile:insertFirstName.placeholder')}
                  isError={Boolean(errors.firstName?.message)}
                  message={errors.firstName?.message}
                  withIcon={firstNameChecking && <Loader />}
                  ref={field.ref}
                  disabled
                />
              )}
            />
          </FormControl>

          <FormControl label={t('profile:lastName.title')} required>
            <Controller
              type='text'
              name='lastName'
              control={control}
              render={(field) => (
                <TextInput
                  className={`${base}__filed-name`}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={async () => {
                    setLastNameChecking(true)
                    await trigger('lastName')
                    setLastNameChecking(false)
                  }}
                  placeholder={t('profile:insertLastName.placeholder')}
                  isError={Boolean(errors.lastName?.message)}
                  message={errors.lastName?.message}
                  withIcon={lastNameChecking && <Loader />}
                  ref={field.ref}
                  disabled
                />
              )}
            />
          </FormControl>
        </div>

        <FormControl label={t('profile:phoneNumber.title')}>
          <InputGroup
            variants='ternary'
            error={errors.countryCode?.message || errors.phoneNumber?.message}
          >
            <Controller
              type='text'
              name='countryCode'
              control={control}
              render={(field) => (
                <Dropdown
                  className='dropdown--telephone'
                  group='right'
                  options={[thaiCountryCodeOption]}
                  selected={findSelected([thaiCountryCodeOption], field.value)}
                  onChange={(value) => setValue('countryCode', value.key)}
                  isError={
                    Boolean(errors.countryCode?.message) ||
                    Boolean(errors.phoneNumber?.message)
                  }
                />
              )}
            />

            <Controller
              name='phoneNumber'
              control={control}
              render={(field) => (
                <TextInput
                  placeholder={t('profile:insertPhoneNumber.placeholder')}
                  type='text'
                  group='left'
                  value={field.value}
                  maxLength={getPhoneNumberMaxLength(field.value)}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e.target.value)
                  }}
                  onBlur={formatPhoneNumberOnBlur((value) => {
                    setValue('phoneNumber', value)
                    trigger('phoneNumber')
                  })}
                  onFocus={() =>
                    setValue('phoneNumber', field.value.replace('-', ''))
                  }
                  onKeyPress={validateKeyPress(new RegExp(/[\p{N}]/u))}
                  onPaste={(e) => handleDisableOnPaste(e)}
                  isError={
                    Boolean(errors.countryCode?.message) ||
                    Boolean(errors.phoneNumber?.message)
                  }
                />
              )}
            />
          </InputGroup>
        </FormControl>

        <FormControl label={t('common:email.label')}>
          <Controller
            as={TextInput}
            type='text'
            name='email'
            control={control}
            placeholder={t('profile:insertEmail.placeholder')}
            maxLength={50}
            isError={Boolean(errors.email?.message)}
            message={errors.email?.message}
            disabled
          />
        </FormControl>

        <FormControl label={t('profile:position.title')} required>
          <Controller
            type='text'
            name='role'
            control={control}
            placeholder={t('profile:insertRole.placeholder')}
            message={errors.role?.message}
            render={({ value }) => (
              <Dropdown
                options={roleOptions}
                selected={findSelected(
                  roleOptions,
                  `${value}`.toLocaleLowerCase(),
                )}
                onChange={handleChangeRole}
                isError={Boolean(errors.role?.message)}
                onBlur={async () => {
                  setRoleChecking(true)
                  await trigger('role')
                  setRoleChecking(false)
                }}
                withIcon={roleChecking && <Loader />}
                fullWidth
                disabled
              />
            )}
          />
        </FormControl>
      </div>
    </div>
  )
}

export default ProfileForm
